import React, { useState, useEffect } from 'react';
import { Modal, Nav, NavItem, Alert } from 'react-bootstrap';
import { filter } from 'fuzzaldrin';

import './ThumbnailModal.scss';
import VideoGrid from './VideoGrid';
import ImagesTab from './ImagesTab';
import { resizeImage } from '../../utils/ImageProcessing';
import { useReplaceThumbnailWithUpload } from './query-hooks/useThumbnail';

const ThumbnailModal = props => {
    const [activeTab, setActiveTab] = useState('videos');
    const [selectedItem, setSelectedItem] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [selectedTimecode, setSelectedTimecode] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // React Query hooks
    const replaceThumbnailWithUploadMutation = useReplaceThumbnailWithUpload();
    
    const handleTabSelect = (key) => {
        setActiveTab(key);
        setSelectedItem(null);
        setSelectedTimecode(null);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setSelectedItem(null);
        setSelectedTimecode(null);
    };

    const filterAssetsBySearch = (assets) => {
        if (!searchTerm.trim()) return assets;
        return filter(assets, searchTerm, { key: 'name' });
    };
    
    const handleSelectVideo = (asset, timecode) => {
        setSelectedItem(asset);
        setSelectedTimecode(timecode);
    };
    
    const handleSelectImage = (asset) => {
        setSelectedItem(asset);
    };
    
    const handleAssignThumbnail = async () => {
        if (!selectedItem) return;
        
        setIsProcessing(true);
        setError(null);
        
        try {
            // Use the passed targetAssetID or fall back to extracting from event
            const targetAssetID = props.targetAssetID;
            const thumbPath = `https://invintus-client-images.s3.amazonaws.com/${props.clientID}/${targetAssetID}.jpg?${new Date().getTime()}`

            if (selectedItem.type === 'video') {
                if (selectedItem.capturedFrame) {
                    const resizedBlob = await resizeImage(selectedItem.capturedFrame.url);
                    const file = new File([resizedBlob], `${targetAssetID}.jpg`, {
                        type: 'image/jpeg',
                        lastModified: new Date().getTime()
                    });

                    // If we have a captured frame from the video, we need to upload it
                    await replaceThumbnailWithUploadMutation.mutateAsync({
                        clientID: props.clientID,
                        assetID: targetAssetID,
                        file: file,
                        eventID: props.event.eventID,
                        path: thumbPath
                    });

                    props.emitSnack({
                        type: 'success',
                        message: 'Thumbnail updated'
                    });
                }
            } else if (selectedItem.type === 'image') {
                const selectedItemImageExt = selectedItem.fileUrl.split('.').pop();
                let imagePath = `https://img.invintus.com/${props.clientID}/${selectedItem.assetID}.${selectedItemImageExt}`;

                if (selectedItem.isUpload) {
                    imagePath = selectedItem.fileUrl;
                }

                const resizedBlob = await resizeImage(imagePath);
                
                // Create a File object from the blob
                const file = new File([resizedBlob], `${targetAssetID}.jpg`, {
                    type: 'image/jpeg',
                    lastModified: new Date().getTime()
                });
                
                // Upload and replace thumbnail in one step
                await replaceThumbnailWithUploadMutation.mutateAsync({
                    clientID: props.clientID,
                    assetID: targetAssetID,
                    file: file,
                    eventID: props.event.eventID,
                    path: thumbPath
                });

                props.emitSnack({
                    type: 'success',
                    message: 'Thumbnail updated'
                });
            }
        } catch (error) {
            console.error('Error assigning thumbnail:', error);
            setError('Failed to assign thumbnail: ' + (error.message || 'Unknown error'));
            setIsProcessing(false);

            props.emitSnack({
                type: 'error',
                message: 'Failed to assign thumbnail: ' + (error.message || 'Unknown error')
            });
        } finally {
            setIsProcessing(false);
            setSelectedItem(null);
            setSelectedTimecode(null);
            props.onClose();
        }
    };
    
    const renderTabs = () => {
        return (
            <Nav bsStyle="tabs" activeKey={activeTab} onSelect={handleTabSelect}>
                <NavItem eventKey="videos">
                    <i className="fa fa-video-camera" /> Videos
                </NavItem>
                <NavItem eventKey="images">
                    <i className="fa fa-image" /> Images
                </NavItem>
            </Nav>
        );
    };
    
    const renderVideosTab = () => {
        const eventVideos = filterAssetsBySearch(props.event.mediaAssets.filter(asset => asset.type === 'video'));
        const libraryVideos = filterAssetsBySearch(props.miscAssets.filter(asset => asset.type === 'video'));

        return (
            <div className="videos-tab">
                <div className="assets-section">
                    <h3>Event Videos</h3>
                    {eventVideos.length === 0 ? (
                        <div className="alert alert-info">
                            <p>{searchTerm ? 'No video assets match your search.' : 'No video assets available in this event.'}</p>
                        </div>
                    ) : (
                        <VideoGrid
                            assets={eventVideos}
                            onSelect={handleSelectVideo}
                            selectedItem={selectedItem}
                            isEventAssets={true}
                            preSelectedAsset={props.preSelectedAsset}
                        />
                    )}
                </div>

                <div className="assets-section">
                    <h3>Media Library Videos</h3>
                    {libraryVideos.length === 0 ? (
                        <div className="alert alert-info">
                            <p>{searchTerm ? 'No video assets match your search.' : 'No video assets available in the media library.'}</p>
                        </div>
                    ) : (
                        <VideoGrid
                            assets={libraryVideos}
                            onSelect={handleSelectVideo}
                            selectedItem={selectedItem}
                            isEventAssets={false}
                        />
                    )}
                </div>
            </div>
        );
    };
    
    const renderTabContent = () => {
        switch (activeTab) {
            case 'videos':
                return renderVideosTab();
            case 'images':
                return (
                    <ImagesTab
                        eventAssets={filterAssetsBySearch(props.event.mediaAssets.filter(asset => asset.type === 'image'))}
                        miscAssets={filterAssetsBySearch(props.miscAssets.filter(asset => asset.type === 'image'))}
                        onSelect={handleSelectImage}
                        selectedItem={selectedItem}
                        getUppy={window.api.getUppy}
                        clientID={props.clientID}
                        eventID={props.event.eventID}
                        searchTerm={searchTerm}
                    />
                );
            default:
                return null;
        }
    };
    
    const renderPreview = () => {
        if (!selectedItem) {
            return null;
        }
        
        if (selectedItem.type === 'video') {
            return (
                <div className="preview-content">
                    {selectedItem.capturedFrame ? (
                        <img 
                            className="preview-image" 
                            src={selectedItem.capturedFrame.url} 
                            alt={`Frame from ${selectedItem.name}`}
                        />
                    ) : (
                        <img 
                            className="preview-image" 
                            src={selectedItem.thumbnail || '/assets/images/video-placeholder.jpg'} 
                            alt={selectedItem.name}
                        />
                    )}
                    <div className="text-center">
                        <strong>Selected:</strong> {selectedItem.name}
                        {selectedTimecode && (
                            <div>
                                <strong>Timecode:</strong> {selectedTimecode}
                            </div>
                        )}
                    </div>
                </div>
            );
        } else if (selectedItem.type === 'image') {
            return (
                <div className="preview-content">
                    <img 
                        className="preview-image" 
                        src={selectedItem.fileUrl || selectedItem.url || '/assets/images/image-placeholder.jpg'} 
                        alt={selectedItem.name}
                    />
                    <div className="text-center">
                        <strong>Selected:</strong> {selectedItem.name}
                    </div>
                </div>
            );
        }
        
        return null;
    };

    return (
        <Modal 
            show={props.show} 
            onHide={props.onClose}
            dialogClassName="thumbnail-modal-dialog"
            backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Assign Thumbnail
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body>
                {error && (
                    <Alert bsStyle="danger" onDismiss={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                
                {renderTabs()}

                {/* Search Bar */}
                <div className="search-section">
                    <div className="input-group">
                        <span className="input-group-addon">
                            <i className="fa fa-search"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search assets..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        {searchTerm && (
                            <span
                                className="input-group-addon clear-search"
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                            >
                                <i className="fa fa-times"></i>
                            </span>
                        )}
                    </div>
                </div>

                <div className="modal-content-layout">
                    <div className={`assets-column ${!selectedItem ? 'full-width' : ''}`}>
                        <div className="tab-content">
                            {renderTabContent()}
                        </div>
                    </div>
                    
                    {selectedItem && (
                        <div className="preview-column">
                            <div className="thumbnail-preview-panel">
                                {renderPreview()}
                            </div>
                        </div>
                    )}
                </div>
            </Modal.Body>
            
            <Modal.Footer>
                <button 
                    className="btn btn-default" 
                    onClick={props.onClose}
                    disabled={isProcessing}
                >
                    Cancel
                </button>
                <button 
                    className="btn btn-primary" 
                    onClick={handleAssignThumbnail}
                    disabled={!selectedItem || isProcessing}
                >
                    {isProcessing ? (
                        <><i className="fa fa-spinner fa-spin" /> Processing...</>
                    ) : (
                        <>Assign Thumbnail</>
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ThumbnailModal;